import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    createAdminProduct,
    updateAdminProduct,
    listAdminProducts,
    listAdminCategories,
    uploadAdminPhoto,
    deleteAdminPhoto,
} from "@/lib/api";
import { Loader2, ArrowLeft, X, AlertCircle, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const MAX_PHOTOS = 4;
const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8 MB

const generateProductId = () => {
    const ts = Date.now().toString(36);
    const rand = Math.random().toString(36).slice(2, 7);
    return `prod-${ts}-${rand}`;
};

const EMPTY = {
    id: generateProductId(),
    name: "",
    category: "",
    subcategoryIds: [],
    kind: "case",
    specs: "",
    accent: "#d4af37",
    price_from: "Sur devis",
    original_price: "",
    photos: [],
    top_selling: false,
    pinned: false,
    active: true,
};

const KINDS = ["case", "charger", "cable", "adapter", "audio", "power", "protection", "accessory"];

// Dropdown with checkboxes + search allowing multiple subcategory (model) selection.
const SubcategoryMultiSelect = ({ options, selectedIds, onChange, disabled }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const selected = options.filter((o) => selectedIds.includes(o.id));
    const filtered = options.filter((o) =>
        o.label.toLowerCase().includes(query.trim().toLowerCase()),
    );

    const toggle = (id) => {
        onChange(
            selectedIds.includes(id)
                ? selectedIds.filter((x) => x !== id)
                : [...selectedIds, id],
        );
    };

    return (
        <div className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((v) => !v)}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 text-sm disabled:cursor-not-allowed disabled:opacity-60"
            >
                <span className={selected.length ? "text-neutral-900" : "text-neutral-400"}>
                    {disabled
                        ? "Choisir d'abord une catégorie"
                        : selected.length === 0
                          ? "Choisir un ou plusieurs modèles"
                          : selected.length <= 2
                            ? selected.map((s) => s.label).join(", ")
                            : `${selected.length} modèles sélectionnés`}
                </span>
                <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && !disabled && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
                        <div className="p-2 border-b border-neutral-100">
                            <input
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Rechercher un modèle…"
                                className="h-8 w-full rounded-md border border-neutral-200 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#d4af37]/30"
                            />
                        </div>
                        <div className="max-h-56 overflow-y-auto p-1">
                            {filtered.length === 0 && (
                                <p className="px-2 py-3 text-center text-xs text-neutral-400">
                                    Aucun résultat
                                </p>
                            )}
                            {filtered.map((o) => (
                                <label
                                    key={o.id}
                                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-neutral-50"
                                >
                                    <Checkbox
                                        checked={selectedIds.includes(o.id)}
                                        onCheckedChange={() => toggle(o.id)}
                                    />
                                    <span>{o.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const ProductForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isEdit = !!id;

    const { data: products = [], isLoading: loadingProducts } = useQuery({
        queryKey: ["admin", "products"],
        queryFn: listAdminProducts,
        enabled: isEdit,
    });
    const { data: categories = [], isLoading: loadingCategories } = useQuery({
        queryKey: ["admin", "categories"],
        queryFn: listAdminCategories,
    });

    const [form, setForm] = useState(() => ({
        ...EMPTY,
        id: isEdit ? "" : generateProductId(),
    }));
    // Files selected by the user but not yet uploaded.
    const [pendingFiles, setPendingFiles] = useState([]);
    const pendingFilesRef = useRef(pendingFiles);
    const [errors, setErrors] = useState([]);
    const [showErrors, setShowErrors] = useState(false);
    useEffect(() => {
        pendingFilesRef.current = pendingFiles;
    }, [pendingFiles]);

    useEffect(() => {
        if (!isEdit) {
            setForm({ ...EMPTY, id: generateProductId() });
            setPendingFiles([]);
            return;
        }
        // Wait for both products and categories so Select components render
        // their options before the value is set.
        if (loadingProducts || loadingCategories) return;
        const existing = products.find((p) => p.id === id);
        if (existing) {
            const { subcategory, subcategories, subcategory_ids, ...rest } = existing;
            setForm({
                ...rest,
                specs: (existing.specs || []).join("\n"),
                photos: existing.photos || [],
                subcategoryIds: existing.subcategory_ids || [],
            });
            setPendingFiles([]);
        }
    }, [isEdit, id, products, categories, loadingProducts, loadingCategories]);

    const availableSubcategories = useMemo(() => {
        const cat = categories.find((c) => c.label === form.category);
        return (cat?.subcategories || [])
            .filter((s) => s.active)
            .sort((a, b) => a.sort_order - b.sort_order);
    }, [categories, form.category]);

    const mutation = useMutation({
        mutationFn: (payload) =>
            isEdit ? updateAdminProduct(id, payload) : createAdminProduct(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
            queryClient.invalidateQueries({ queryKey: ["catalog"] });
            toast.success(isEdit ? "Produit mis à jour." : "Produit créé.");
            navigate("/admin/products");
        },
        onError: (err) => {
            const detail = err.response?.data?.detail;
            if (detail) {
                showErrorPopup(
                    Array.isArray(detail)
                        ? detail.map((e) => (typeof e === "string" ? e : e.msg || JSON.stringify(e)))
                        : [detail]
                );
            } else {
                showErrorPopup("Une erreur est survenue lors de l'enregistrement.");
            }
        },
    });

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handlePhotoChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const remainingSlots = MAX_PHOTOS - form.photos.length - pendingFiles.length;
        if (remainingSlots <= 0) {
            showErrorPopup([`Vous pouvez ajouter jusqu'à ${MAX_PHOTOS} photos.`]);
            e.target.value = "";
            return;
        }
        const oversized = files.filter((file) => file.size > MAX_FILE_SIZE);
        if (oversized.length > 0) {
            showErrorPopup(
                oversized.map(
                    (file) => `Le fichier "${file.name}" dépasse la taille maximale de 8 Mo.`
                )
            );
            e.target.value = "";
            return;
        }
        const toAdd = files.slice(0, remainingSlots);
        const objectUrls = toAdd.map((file) => URL.createObjectURL(file));
        setPendingFiles((prev) =>
            prev.concat(
                toAdd.map((file, i) => ({ file, preview: objectUrls[i] }))
            )
        );
        // Reset input so the same file can be selected again if removed.
        e.target.value = "";
    };

    const handleRemoveExistingPhoto = async (index) => {
        const url = form.photos[index];
        if (url && url.startsWith("/uploads/")) {
            const filename = url.split("/").pop();
            if (filename) {
                try {
                    await deleteAdminPhoto(filename, id);
                    toast.success("Photo supprimée du serveur.");
                } catch (err) {
                    toast.error(err.response?.data?.detail || "Impossible de supprimer la photo.");
                    return;
                }
            }
        }
        setForm((f) => ({
            ...f,
            photos: f.photos.filter((_, i) => i !== index),
        }));
    };

    const handleRemovePendingPhoto = (index) => {
        setPendingFiles((prev) => {
            const removed = prev[index];
            if (removed?.preview) URL.revokeObjectURL(removed.preview);
            return prev.filter((_, i) => i !== index);
        });
    };

    const validateForm = () => {
        const nextErrors = [];
        if (!form.id.trim()) nextErrors.push("L'ID technique est obligatoire.");
        if (!form.name.trim()) nextErrors.push("Le nom du produit est obligatoire.");
        if (!form.category) nextErrors.push("La catégorie est obligatoire.");
        if (!form.kind) nextErrors.push("Le visuel (kind) est obligatoire.");
        const oversized = pendingFiles.filter(({ file }) => file.size > MAX_FILE_SIZE);
        oversized.forEach(({ file }) =>
            nextErrors.push(`Le fichier "${file.name}" dépasse 8 Mo.`)
        );
        return nextErrors;
    };

    const showErrorPopup = (messages) => {
        setErrors(Array.isArray(messages) ? messages : [messages]);
        setShowErrors(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            showErrorPopup(validationErrors);
            return;
        }

        let uploadedUrls = [];
        if (pendingFiles.length > 0) {
            try {
                const uploads = await Promise.all(
                    pendingFiles.map(({ file }) => uploadAdminPhoto(file))
                );
                uploadedUrls = uploads.map((u) => u.url);
            } catch (err) {
                showErrorPopup(err.response?.data?.detail || "Échec de l'upload des photos.");
                return;
            }
        }

        const photos = [...form.photos, ...uploadedUrls].slice(0, MAX_PHOTOS);
        const selectedLabels = availableSubcategories
            .filter((s) => form.subcategoryIds.includes(s.id))
            .map((s) => s.label);
        const processed = {
            photos,
            subcategory_ids: form.subcategoryIds,
            subcategory: selectedLabels[0] || "",
            specs: form.specs
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean),
            tagline: form.tagline || "",
        };
        // The backend requires an id when creating a product.
        // On edit the id comes from the URL, so we strip it from the body.
        const payload = isEdit
            ? { ...form, ...processed, id: undefined }
            : { ...form, ...processed };
        mutation.mutate(payload);
    };

    // Cleanup object URLs on unmount.
    useEffect(() => {
        return () => {
            pendingFilesRef.current.forEach(({ preview }) => {
                if (preview) URL.revokeObjectURL(preview);
            });
        };
    }, []);

    const totalPhotos = form.photos.length + pendingFiles.length;

    if (isEdit && (loadingProducts || loadingCategories)) {
        return (
            <AdminLayout>
                <div className="flex justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="max-w-2xl">
                <Button
                    variant="ghost"
                    className="mb-4 -ml-2 text-neutral-600"
                    onClick={() => navigate("/admin/products")}
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour aux produits
                </Button>

                <h1 className="font-display text-3xl font-semibold tracking-tight">
                    {isEdit ? "Modifier le produit" : "Nouveau produit"}
                </h1>
                <p className="text-neutral-500 mt-1">
                    Renseignez les informations du produit.
                </p>

                <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5">
                    <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1.5">
                            <Label>ID technique (auto)</Label>
                            <Input
                                value={form.id}
                                onChange={update("id")}
                                disabled={isEdit}
                                placeholder="Généré automatiquement"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Nom</Label>
                            <Input
                                value={form.name}
                                onChange={update("name")}
                                placeholder="Coque iPhone 15 Pro"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1.5">
                            <Label>Catégorie</Label>
                            <Select
                                key={`cat-${form.category}-${categories.length}`}
                                value={form.category}
                                onValueChange={(v) =>
                                    setForm((f) => ({ ...f, category: v, subcategoryIds: [] }))
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Choisir une catégorie" />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map((c) => (
                                        <SelectItem key={c.id} value={c.label}>
                                            {c.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Sous-catégories (modèles)</Label>
                            <SubcategoryMultiSelect
                                options={availableSubcategories}
                                selectedIds={form.subcategoryIds}
                                onChange={(ids) => setForm((f) => ({ ...f, subcategoryIds: ids }))}
                                disabled={!form.category}
                            />
                            <p className="text-xs text-neutral-500">
                                Plusieurs modèles peuvent être sélectionnés pour un même produit.
                            </p>
                        </div>

                        <div className="space-y-1.5">
                            <Label>Visuel (kind)</Label>
                            <Select
                                key={`kind-${form.kind}`}
                                value={form.kind}
                                onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Choisir un visuel" />
                                </SelectTrigger>
                                <SelectContent>
                                    {KINDS.map((k) => (
                                        <SelectItem key={k} value={k}>
                                            {k}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <Label>Photos du produit</Label>
                            <span className="text-xs text-neutral-500">
                                {totalPhotos} / {MAX_PHOTOS}
                            </span>
                        </div>
                        <Input
                            type="file"
                            multiple
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            onChange={handlePhotoChange}
                            disabled={totalPhotos >= MAX_PHOTOS}
                        />
                        <p className="text-xs text-neutral-500">
                            Formats acceptés : JPEG, PNG, WebP, GIF. Max 8 Mo par fichier.
                            {totalPhotos >= MAX_PHOTOS && " Limite atteinte."}
                        </p>

                        {(form.photos.length > 0 || pendingFiles.length > 0) && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                                {form.photos.map((url, idx) => (
                                    <div
                                        key={`existing-${idx}`}
                                        className="relative aspect-square rounded-xl border border-neutral-200 overflow-hidden bg-neutral-50"
                                    >
                                        <img
                                            src={url}
                                            alt={`Photo ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveExistingPhoto(idx)}
                                            className="absolute top-1 right-1 bg-white/90 hover:bg-white border border-neutral-200 rounded-full p-1 shadow-sm text-red-600"
                                            aria-label="Supprimer la photo"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                                {pendingFiles.map(({ preview }, idx) => (
                                    <div
                                        key={`pending-${idx}`}
                                        className="relative aspect-square rounded-xl border border-neutral-200 overflow-hidden bg-neutral-50"
                                    >
                                        <img
                                            src={preview}
                                            alt={`Nouvelle photo ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black/10" />
                                        <button
                                            type="button"
                                            onClick={() => handleRemovePendingPhoto(idx)}
                                            className="absolute top-1 right-1 bg-white/90 hover:bg-white border border-neutral-200 rounded-full p-1 shadow-sm text-red-600"
                                            aria-label="Retirer la photo"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <Label>Spécifications (une par ligne)</Label>
                        <Textarea
                            value={form.specs}
                            onChange={update("specs")}
                            rows={4}
                            placeholder="Compatible iPhone 15 Pro\nSilicone premium\nProtection anti-choc"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1.5">
                            <Label>Couleur d'accent</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="color"
                                    value={form.accent}
                                    onChange={update("accent")}
                                    className="w-12 h-10 p-1"
                                />
                                <Input value={form.accent} onChange={update("accent")} />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Prix à partir de</Label>
                            <Input
                                value={form.price_from}
                                onChange={update("price_from")}
                                placeholder="Sur devis"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Prix initial (barré)</Label>
                            <Input
                                value={form.original_price}
                                onChange={update("original_price")}
                                placeholder="Ex: 5000"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="top_selling"
                            checked={form.top_selling}
                            onCheckedChange={(v) => setForm((f) => ({ ...f, top_selling: !!v }))}
                        />
                        <Label htmlFor="top_selling" className="cursor-pointer">
                            Marquer comme Top vente
                        </Label>
                    </div>

                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="pinned"
                            checked={form.pinned}
                            onCheckedChange={(v) => setForm((f) => ({ ...f, pinned: !!v }))}
                        />
                        <Label htmlFor="pinned" className="cursor-pointer">
                            Épingler en tête de liste
                        </Label>
                    </div>

                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="active"
                            checked={form.active}
                            onCheckedChange={(v) => setForm((f) => ({ ...f, active: !!v }))}
                        />
                        <Label htmlFor="active" className="cursor-pointer">
                            Produit actif et visible sur le site
                        </Label>
                    </div>

                    <div className="pt-4 flex items-center gap-3">
                        <Button
                            type="submit"
                            disabled={mutation.isPending}
                            className="bg-[#1C1C1E] hover:bg-black rounded-full"
                        >
                            {mutation.isPending && (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            )}
                            {isEdit ? "Enregistrer" : "Créer le produit"}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => navigate("/admin/products")}
                        >
                            Annuler
                        </Button>
                    </div>
                </form>

                <Dialog open={showErrors} onOpenChange={setShowErrors}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-red-600">
                                <AlertCircle className="w-5 h-5" />
                                Erreur de validation
                            </DialogTitle>
                            <DialogDescription>
                                Veuillez corriger les points suivants avant de continuer :
                            </DialogDescription>
                        </DialogHeader>
                        <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700 mt-2">
                            {errors.map((err, i) => (
                                <li key={i}>{err}</li>
                            ))}
                        </ul>
                        <div className="mt-4 flex justify-end">
                            <Button onClick={() => setShowErrors(false)}>Fermer</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
};

export default ProductForm;
